<script setup lang="ts">
// The day/month/run/run-count/max-tier fields for one spend cap: identical markup in the caps
// table's per-row edit form and the "Add or replace a cap" form below it (SpendView.vue),
// differing only in which draft object they edit. Every field is a kit control: the kit owns
// their surface, edge, radius and height, so nothing here states a theme value of its own.

import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhFieldError,
    ZhFormField,
} from '@zerohuman/ui'
import { onMounted, toRef } from 'vue'
import { MODEL_TIERS, modelTierLabel } from '@/api'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { optionalAmount } from '@/lib/fieldRules'
import { useLlmTiers } from '@/lib/llmTiers'

/**
 * #1756: in the "Add or replace a cap" form (`helped`) each field is the shared kit field with one line of help, and
 * the form's own action row carries Save. A caps-table row is an inline editor (out of that issue's scope), so it
 * keeps its compact fields and its own Save.
 */
const HELP = {
    day: { help: 'The most it may spend in one day, in dollars.', example: '50.00' },
    month: { help: 'The most it may spend in one month, in dollars.', example: '1000.00' },
    run: { help: 'The most a single run may spend, in dollars.', example: '5.00' },
    runs: { help: 'How many runs it may start in one day.', example: '10' },
    tier: { help: 'The most capable kind of model its runs may use.', example: 'mid' },
} as const

export type CapDraft = {
    layer: string
    layerId: string
    moneyDay: string
    moneyMonth: string
    moneyRun: string
    runCountDay: string
    maxModelTier: string
}

const props = defineProps<{
    draft: CapDraft
    helped?: boolean
    /** Prefixes the field ids, so the rows and the add form on one page never share one. */
    idPrefix?: string
}>()

// #664 item 1: a tier is a label until you can see the model behind it for *this* tenant.
const { tierModelLabel, loadLlmTiers } = useLlmTiers()
onMounted(loadLlmTiers)

type LimitField = 'moneyDay' | 'moneyMonth' | 'moneyRun' | 'runCountDay'

/**
 * A limit as it was typed. The kit `Input` hands a number field's value up as a number, so "50.00" would come back as
 * "50" on the next render; the draft holds strings, and Edit on the save preview (#1756) must give back exactly what
 * was typed. The text is read from the field itself; a value set on the Input directly (a string) is taken as it is.
 */
function typed(field: LimitField, event: Event): void {
    props.draft[field] = (event.target as HTMLInputElement).value
}

function setLimit(field: LimitField, value: unknown): void {
    if (typeof value === 'string') props.draft[field] = value
}

/**
 * Each amount is blank or 0 or more (the fields' own `min="0"`), checked while typing (#1757). The form that
 * holds these fields calls `check` before it saves.
 */
const fields = useFieldValidation(
    toRef(props, 'draft'),
    {
        moneyDay: optionalAmount('Day $'),
        moneyMonth: optionalAmount('Month $'),
        moneyRun: optionalAmount('Run $'),
        runCountDay: optionalAmount('Runs / day', '10'),
    },
    { idPrefix: props.idPrefix ?? 'cap' },
)
defineExpose({ check: fields.check })

/** The tier picker's choice, written back to the draft as the tier it names. */
function chooseTier(value: unknown): void {
    props.draft.maxModelTier = value === null || value === undefined ? '' : String(value)
}
</script>

<template>
	<!--
		#1757's amount checks on the shared field (CTO decisions 18 and 20 on #1756): each limit keeps its fixed id, is
		described by its error first and its help line after, and the error sits under the help line.
	-->
	<template v-if="helped">
		<ZhFormField :id="fields.fieldId('moneyDay')" :error-id="fields.message('moneyDay').id" label="Day $" :help="HELP.day.help" :example="HELP.day.example">
			<template #default="{ controlId, describedBy }">
				<Input
					:model-value="draft.moneyDay"
					type="number"
					step="0.01"
					min="0"
					class="w-24"
					v-bind="fields.bind('moneyDay')"
					:id="controlId"
					:aria-describedby="describedBy"
					@input="typed('moneyDay', $event)"
					@update:model-value="setLimit('moneyDay', $event)"
				/>
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('moneyDay')" />
			</template>
		</ZhFormField>
		<ZhFormField :id="fields.fieldId('moneyMonth')" :error-id="fields.message('moneyMonth').id" label="Month $" :help="HELP.month.help" :example="HELP.month.example">
			<template #default="{ controlId, describedBy }">
				<Input
					:model-value="draft.moneyMonth"
					type="number"
					step="0.01"
					min="0"
					class="w-24"
					v-bind="fields.bind('moneyMonth')"
					:id="controlId"
					:aria-describedby="describedBy"
					@input="typed('moneyMonth', $event)"
					@update:model-value="setLimit('moneyMonth', $event)"
				/>
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('moneyMonth')" />
			</template>
		</ZhFormField>
		<ZhFormField :id="fields.fieldId('moneyRun')" :error-id="fields.message('moneyRun').id" label="Run $" :help="HELP.run.help" :example="HELP.run.example">
			<template #default="{ controlId, describedBy }">
				<Input
					:model-value="draft.moneyRun"
					type="number"
					step="0.01"
					min="0"
					class="w-24"
					v-bind="fields.bind('moneyRun')"
					:id="controlId"
					:aria-describedby="describedBy"
					@input="typed('moneyRun', $event)"
					@update:model-value="setLimit('moneyRun', $event)"
				/>
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('moneyRun')" />
			</template>
		</ZhFormField>
		<ZhFormField :id="fields.fieldId('runCountDay')" :error-id="fields.message('runCountDay').id" label="Runs / day" :help="HELP.runs.help" :example="HELP.runs.example">
			<template #default="{ controlId, describedBy }">
				<Input
					:model-value="draft.runCountDay"
					type="number"
					min="0"
					class="w-20"
					v-bind="fields.bind('runCountDay')"
					:id="controlId"
					:aria-describedby="describedBy"
					@input="typed('runCountDay', $event)"
					@update:model-value="setLimit('runCountDay', $event)"
				/>
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('runCountDay')" />
			</template>
		</ZhFormField>
		<ZhFormField v-slot="{ controlId, helpId }" label="Max model tier" :help="HELP.tier.help" :example="HELP.tier.example">
			<Select :model-value="draft.maxModelTier" name="maxModelTier" @update:model-value="chooseTier">
				<SelectTrigger :id="controlId" :aria-describedby="helpId">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="tier in MODEL_TIERS" :key="tier" :value="tier">{{ modelTierLabel(tier) }}</SelectItem>
				</SelectContent>
			</Select>
			<span data-testid="tier-model" class="text-muted-foreground block text-xs">
				{{ tierModelLabel(props.draft.maxModelTier) }}. Applies only when a task leaves its model on Default.
			</span>
		</ZhFormField>
	</template>
	<template v-else>
	<!-- Each message sits in its field's label, so the fields stay one row of labelled controls. -->
	<label class="grid gap-1 text-sm">
		<span class="text-muted-foreground">Day $</span>
		<Input v-model="draft.moneyDay" type="number" step="0.01" min="0" class="w-24" v-bind="fields.bind('moneyDay')" />
		<ZhFieldError v-bind="fields.message('moneyDay')" />
	</label>
	<label class="grid gap-1 text-sm">
		<span class="text-muted-foreground">Month $</span>
		<Input v-model="draft.moneyMonth" type="number" step="0.01" min="0" class="w-24" v-bind="fields.bind('moneyMonth')" />
		<ZhFieldError v-bind="fields.message('moneyMonth')" />
	</label>
	<label class="grid gap-1 text-sm">
		<span class="text-muted-foreground">Run $</span>
		<Input v-model="draft.moneyRun" type="number" step="0.01" min="0" class="w-24" v-bind="fields.bind('moneyRun')" />
		<ZhFieldError v-bind="fields.message('moneyRun')" />
	</label>
	<label class="grid gap-1 text-sm">
		<span class="text-muted-foreground">Runs / day</span>
		<Input v-model="draft.runCountDay" type="number" min="0" class="w-20" v-bind="fields.bind('runCountDay')" />
		<ZhFieldError v-bind="fields.message('runCountDay')" />
	</label>
	<!--
		`name` makes the tier a field of the form it sits in: inside a form, Reka then draws its own hidden native
		select for the choice, which is how the browser (and autofill) reads and sets it.
	-->
	<label class="grid gap-1 text-sm">
		<span class="text-muted-foreground">Max model tier</span>
		<Select :model-value="draft.maxModelTier" name="maxModelTier" @update:model-value="chooseTier">
			<SelectTrigger>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem v-for="tier in MODEL_TIERS" :key="tier" :value="tier">{{ modelTierLabel(tier) }}</SelectItem>
			</SelectContent>
		</Select>
		<span data-testid="tier-model" class="text-muted-foreground block text-xs">
			{{ tierModelLabel(props.draft.maxModelTier) }}. Applies only when a task leaves its model on Default.
		</span>
	</label>
	<Button type="submit" size="sm">Save</Button>
	</template>
</template>
